import { useEffect, useState } from 'react';
import { useCheckpointStore } from '../core/store/checkpointStore';
import { EXPERIENCES } from '../content/experiences';

/**
 * Info card that slides up when the character walks into a checkpoint,
 * plus a small HUD with discovery progress and nearest-beacon hint.
 */
export function CheckpointCard() {
  const activeId = useCheckpointStore((state) => state.activeId);
  const visitedIds = useCheckpointStore((state) => state.visitedIds);
  const nearestId = useCheckpointStore((state) => state.nearestId);
  const nearestDistance = useCheckpointStore((state) => state.nearestDistance);

  // Keep the last entry rendered while the card animates out
  const [displayedId, setDisplayedId] = useState<string | null>(null);
  useEffect(() => {
    if (activeId) setDisplayedId(activeId);
  }, [activeId]);

  const entry = EXPERIENCES.find((e) => e.id === displayedId);
  const nearest = EXPERIENCES.find((e) => e.id === nearestId);
  const isOpen = activeId !== null && entry != null;

  const mono: React.CSSProperties = {
    fontFamily: 'Cousine, monospace',
    color: 'white',
    letterSpacing: '0.08em',
  };

  return (
    <>
      {/* Discovery counter, top center */}
      <div
        style={{
          ...mono,
          position: 'absolute',
          top: 24,
          left: '50%',
          transform: 'translateX(-50%)',
          fontSize: 11,
          opacity: 0.7,
        }}
      >
        MEMORIES {visitedIds.length} / {EXPERIENCES.length}
      </div>

      {/* Nearest beacon hint, bottom center (hidden while the card is open) */}
      <div
        style={{
          ...mono,
          position: 'absolute',
          bottom: 24,
          left: '50%',
          transform: 'translateX(-50%)',
          fontSize: 11,
          opacity: !isOpen && nearest && isFinite(nearestDistance) ? 0.55 : 0,
          transition: 'opacity 0.4s ease',
          whiteSpace: 'nowrap',
        }}
      >
        NEAREST SIGNAL: {nearest?.org.toUpperCase()} · {Math.round(nearestDistance)}M
      </div>

      {/* Info card */}
      <div
        style={{
          ...mono,
          position: 'absolute',
          bottom: 56,
          left: '50%',
          transform: `translateX(-50%) translateY(${isOpen ? 0 : 24}px)`,
          opacity: isOpen ? 1 : 0,
          transition: 'opacity 0.35s ease, transform 0.35s ease',
          pointerEvents: isOpen ? 'auto' : 'none',
          width: 'min(440px, calc(100vw - 48px))',
          background: 'rgba(0, 0, 0, 0.78)',
          border: '1px solid rgba(255, 255, 255, 0.25)',
          backdropFilter: 'blur(8px)',
          padding: '22px 26px',
        }}
      >
        {entry && (
          <>
            <div
              style={{
                fontSize: 10,
                opacity: 0.6,
                marginBottom: 10,
                display: 'flex',
                justifyContent: 'space-between',
                gap: 12,
              }}
            >
              <span>{entry.org.toUpperCase()}</span>
              <span>{entry.year.toUpperCase()}</span>
            </div>
            <div
              style={{
                fontSize: 17,
                marginBottom: entry.role ? 6 : 12,
                color: entry.color,
              }}
            >
              {entry.title.toUpperCase()}
            </div>
            {entry.role && (
              <div style={{ fontSize: 11, opacity: 0.75, marginBottom: 12 }}>
                {entry.role}
              </div>
            )}
            <div style={{ fontSize: 12, lineHeight: 1.7, opacity: 0.85, letterSpacing: '0.04em' }}>
              {entry.description}
            </div>
            {entry.link && (
              <a
                href={entry.link.url}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  ...mono,
                  display: 'inline-block',
                  marginTop: 14,
                  fontSize: 11,
                  padding: '8px 14px',
                  border: `1px solid ${entry.color}`,
                  color: entry.color,
                  textDecoration: 'none',
                }}
              >
                {entry.link.label.toUpperCase()} ↗
              </a>
            )}
            <div style={{ fontSize: 9, opacity: 0.4, marginTop: 16 }}>
              WALK AWAY TO CLOSE
            </div>
          </>
        )}
      </div>
    </>
  );
}
